import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useTenant } from "./useTenant";
import { isOfficeReadOnly } from "@/lib/role-access";

type AppRole = "admin" | "driver" | "dispatcher" | "service_technician" | "terminal_staff" | "analyst";

export function useUserRole() {
  const { user } = useAuth();
  const { activeTenant } = useTenant();

  const { data: roles = [], isLoading } = useQuery({
    queryKey: ["user-roles", user?.id, activeTenant],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase.rpc("get_user_tenant_roles" as any, { _user_id: user.id });
      if (error) throw error;
      return (data as AppRole[] | null) ?? [];
    },
    enabled: !!user?.id,
  });

  const isAdmin = roles.includes("admin");
  const isAnalyst = roles.length === 1 && roles[0] === "analyst";
  // Canonical office read-only gate: analyst without admin/dispatcher.
  // Yard surfaces additionally check canUseYard(roles).
  const isReadOnly = isOfficeReadOnly(roles);
  // Backward compat: primary role (first non-analyst, or analyst if only role)
  const role = roles.length > 0 ? (roles.find(r => r !== "analyst") ?? roles[0]) : null;

  return { roles, role, isAdmin, isAnalyst, isReadOnly, isLoading };
}
